import { useState, useEffect, useCallback } from "react";

import toastError from "../../errors/toastError";
import {
  findPrompts,
  findQueueIntegrations,
  findFlowBuilders,
  findAllQueues,
} from "../../services/queue.api";

// D8.1.4b — Catálogos/datos de referencia del modal de colas (read-only):
// prompts, integraciones, flujos FlowBuilder y TODOS los departamentos (aiQueues,
// con sincronización en tiempo real vía socket). IN: open, socketManager.
export default function useQueueCatalogs({ open, socketManager }) {
  const [prompts, setPrompts] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [flowBuilders, setFlowBuilders] = useState([]);
  const [loadingFlowBuilders, setLoadingFlowBuilders] = useState(false);
  const [aiQueues, setAiQueues] = useState([]);

  // Prompts (IA)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await findPrompts();
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // Integraciones de cola
  useEffect(() => {
    (async () => {
      try {
        const { data } = await findQueueIntegrations();
        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  // TODOS los departamentos (no solo los con IA)
  const loadAIQueues = useCallback(async () => {
    try {
      const { data } = await findAllQueues();
      setAiQueues(data || []);
    } catch (err) {
      console.error("Error cargando departamentos:", err);
      setAiQueues([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadAIQueues();
    }
  }, [open, loadAIQueues]);

  // Flujos FlowBuilder activos
  useEffect(() => {
    if (!open) return;

    const loadFlowBuilders = async () => {
      try {
        setLoadingFlowBuilders(true);
        const { data } = await findFlowBuilders({ active: true });
        setFlowBuilders(data?.records || []);
      } catch (err) {
        console.error("Error cargando flujos FlowBuilder:", err);
        setFlowBuilders([]);
      } finally {
        setLoadingFlowBuilders(false);
      }
    };

    loadFlowBuilders();
  }, [open]);

  // Sincronización en tiempo real de departamentos vía socket
  useEffect(() => {
    if (!socketManager || !open) return;

    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    const handleQueueUpdate = (data) => {
      if (data.action === "update" && data.queue) {
        const updatedQueue = data.queue;
        setAiQueues((prevQueues) => {
          const existingIndex = prevQueues.findIndex(q => q.id === updatedQueue.id);
          const entry = {
            id: updatedQueue.id,
            name: updatedQueue.name,
            promptId: updatedQueue.promptId,
            integrationId: updatedQueue.integrationId,
            flowBuilderId: updatedQueue.flowBuilderId
          };
          if (existingIndex >= 0) {
            const next = [...prevQueues];
            next[existingIndex] = entry;
            return next;
          }
          return [...prevQueues, entry];
        });
      } else if (data.action === "create" && data.queue) {
        setAiQueues((prevQueues) => {
          const exists = prevQueues.some(q => q.id === data.queue.id);
          if (!exists) {
            return [
              ...prevQueues,
              {
                id: data.queue.id,
                name: data.queue.name,
                promptId: data.queue.promptId,
                integrationId: data.queue.integrationId,
                flowBuilderId: data.queue.flowBuilderId
              }
            ];
          }
          return prevQueues;
        });
      } else if (data.action === "delete" && data.queueId) {
        setAiQueues((prevQueues) => {
          return prevQueues.filter(q => q.id !== data.queueId);
        });
      }
    };

    socket.on(`company-${companyId}-queue`, handleQueueUpdate);

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-queue`, handleQueueUpdate);
      }
    };
  }, [socketManager, open]);

  return { prompts, integrations, flowBuilders, loadingFlowBuilders, aiQueues };
}
